import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { useNavigate, useParams } from 'react-router-dom';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AvatarFavoriteButton } from 'us-react-components/dist/AvatarFavoriteButton/AvatarFavoriteButton';
import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import { MessageBubble } from 'us-react-components/dist/MessageBubble/MessageBubble';
import { MessageComposer } from 'us-react-components/dist/MessageComposer/MessageComposer';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { PrivateNoteSection } from 'us-react-components/dist/PrivateNoteSection/PrivateNoteSection';
import { SitterNoteModal } from 'us-react-components/dist/SitterNoteModal/SitterNoteModal';
import {
  FavoriteSitterService,
  MessageThreadService,
  Sitter,
  SitterService,
  ThreadedMessage,
  ThreadedMessageService,
} from 'us-web-services';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { checkForPaywall } from '../../utils/PaywallUtil';
import { DisplayUtil } from '../../utils/DisplayUtil';

const MAX_MESSAGE_LENGTH = 3000;

interface SitterWithRelationship extends Omit<Sitter, 'relationship' | 'address'> {
  active?: boolean;
  hiddenReason?: number;
  relationship?: {
    bookmarked?: boolean;
    bookmarkedId?: number;
    bookingCount?: number;
    distance?: string | number;
    parentNote?: { id?: number; comment?: string; updated?: string };
  };
  address?: { city?: string; state?: string; zip?: string };
}

// Mirrors Angular UtilityService.parseApprovedUrls — here we just escape the
// text and leave it as plain text. Angular also ran the output through
// $sanitize; linkification is a follow-up nicety.
const parseMessage = (msg?: string): string => msg ?? '';

export const MessageThread = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const navigate = useNavigate();
  const { id: pathId, sitterId: sitterIdParam } = useParams<{
    id: string;
    sitterId: string;
  }>();
  const { stores, services: { config } } = useDependencies();
  const modal = useModal();
  const user = stores.user.user;

  const parentId = Number(pathId);
  const sitterId = Number(sitterIdParam);

  const [sitter, setSitter] = useState<SitterWithRelationship | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [messages, setMessages] = useState<ThreadedMessage[]>([]);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [newMessage, setNewMessage] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [threadId, setThreadId] = useState<number | undefined>(undefined);
  const [showHiddenModal, setShowHiddenModal] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - Message Thread';
  }, []);

  useEffect(() => {
    if (!user?.id || !parentId || !sitterId) return;
    if (parentId !== user.id) {
      navigate(`/parent/${user.id}/messages`);
      return;
    }

    void (async (): Promise<void> => {
      try {
        const resp = await SitterService.get(sitterId, { params: { userId: user.id, parentId: user.id } });
        setSitter((resp.data?.data ?? null) as SitterWithRelationship | null);
      } catch (err) {
        console.warn('[MessageThread] sitter load failed', err);
      }
    })();

    const fetchInitial = async (): Promise<void> => {
      try {
        const resp = await ThreadedMessageService.getByFilter({
          params: { parentId, sitterId, userId: user.id },
        });
        const data = resp.data?.data ?? [];
        setMessages(data);
        setTotalSize(resp.data?.meta?.size ?? data.length);
        const firstThreadId = data[0]?.threadId;
        if (firstThreadId) {
          setThreadId(firstThreadId);
          // mark as read
          try {
            await MessageThreadService.patch(firstThreadId, {
              id: firstThreadId,
              read: new Date().toISOString(),
            }, {
              params: {
                parentId,
                sitterId,
                userId: user.id,
              },
            });
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.warn('[MessageThread] fetch failed', err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchInitial();
  }, [user?.id, parentId, sitterId]);

  // Auto-scroll to last message once loaded.
  useEffect(() => {
    if (!isLoading && messages.length > 0 && endRef.current) {
      endRef.current.scrollIntoView({ block: 'end' });
    }
  }, [isLoading, messages.length]);

  // Preserve scroll position when older messages are prepended via fetchPrevious
  // — otherwise the user gets snapped to the top.
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const preserveScrollHeightRef = useRef<number | null>(null);
  useEffect(() => {
    const container = messagesScrollRef.current;
    const previousHeight = preserveScrollHeightRef.current;
    if (container && previousHeight != null) {
      container.scrollTop = container.scrollHeight - previousHeight;
      preserveScrollHeightRef.current = null;
    }
  }, [messages.length]);

  const fetchPrevious = async (): Promise<void> => {
    if (!messages.length || !user?.id) return;
    const lastId = messages[0].id;
    if (messagesScrollRef.current) {
      preserveScrollHeightRef.current = messagesScrollRef.current.scrollHeight;
    }
    try {
      const resp = await ThreadedMessageService.getByFilter({
        params: {
          parentId,
          sitterId,
          userId: user.id,
          q: `id.lt(${lastId})`,
        },
      });
      const older = resp.data?.data ?? [];
      setMessages((prev) => [...older, ...prev]);
    } catch (err) {
      console.warn('[MessageThread] fetchPrevious failed', err);
    }
  };

  const sendMessage = async (body: string): Promise<boolean> => {
    if (!sitter?.id || !user?.id) return false;
    try {
      const resp = await ThreadedMessageService.create({
        parent: { id: parentId },
        sitter: { id: sitter.id },
        authorId: user.id,
        message: body,
        ...(threadId ? { threadId } : {}),
      } as unknown as ThreadedMessage);
      const saved = resp.data?.data;
      if (saved) {
        setMessages((prev) => [...prev, saved]);
        setTotalSize((s) => s + 1);
        if (!threadId && saved.threadId) setThreadId(saved.threadId);
      }
      return true;
    } catch (err) {
      const errors = (err as { response?: { data?: { errors?: Array<{ code?: string; message?: string }> } } })?.response?.data?.errors;
      const isPlanError = errors?.some((e) => e.code && e.code.indexOf('threadedMessage.plan') > -1);
      if (isPlanError) {
        setPaywallOpen(true);
      } else {
        toast.error(errors?.[0]?.message ?? t('pages.messageThread.sendFailed'));
      }
      return false;
    }
  };

  const toggleFavorite = async (): Promise<void> => {
    if (!sitter?.id || !user?.id) return;
    try {
      if (sitter.relationship?.bookmarked && sitter.relationship.bookmarkedId) {
        await FavoriteSitterService.delete(sitter.relationship.bookmarkedId);
        setSitter((prev) =>
          prev ? { ...prev, relationship: { ...(prev.relationship ?? {}), bookmarked: false, bookmarkedId: undefined } } : prev,
        );
      } else {
        const resp = await FavoriteSitterService.create({ sitterId: sitter.id, parentId: user.id } as never);
        const created = resp.data?.data as { id?: number } | undefined;
        setSitter((prev) =>
          prev ? { ...prev, relationship: { ...(prev.relationship ?? {}), bookmarked: true, bookmarkedId: created?.id } } : prev,
        );
      }
    } catch {
      toast.error(t('pages.messageThread.sendFailed'));
    }
  };

  const bookSitter = (): void => {
    if (!sitter?.id) return;
    stores.verification.checkVerification(() => {
      checkForPaywall({
        userId: user?.id,
        sitter: sitter as Sitter,
        action: (): void => {
          navigate(`/parent/bookings/select?sitterId=${sitter.id}`);
        },
        openPaywall: (): void => setPaywallOpen(true),
      });
    }, (): void => modal?.setModalName(ModalName.IdentityVerification));
  };

  const interviewSitter = (): void => {
    if (!sitter?.id) return;
    stores.verification.checkVerification(() => {
      navigate(`/parent/interviews/select?sitterId=${sitter.id}`);
    }, (): void => modal?.setModalName(ModalName.IdentityVerification));
  };

  const onNoteSaved = (note?: { id?: number; comment?: string }): void => {
    setSitter((prev) =>
      prev ? { ...prev, relationship: { ...(prev.relationship ?? {}), parentNote: note } } : prev,
    );
  };

  const createMessage = async (): Promise<void> => {
    if (!newMessage.trim() || isSending || !sitter?.id || !user?.id) return;
    if (sitter.hiddenReason !== 0 && sitter.hiddenReason !== undefined) {
      setShowHiddenModal(true);
      return;
    }
    setIsSending(true);
    const body = newMessage;
    const ok = await sendMessage(body);
    if (ok) setNewMessage('');
    setIsSending(false);
  };

  const handlePaywallSuccess = async (): Promise<void> => {
    setPaywallOpen(false);
    const body = newMessage;
    if (!body.trim()) return;
    setIsSending(true);
    const ok = await sendMessage(body);
    if (ok) setNewMessage('');
    setIsSending(false);
  };

  const isSameSender = (index: number): boolean => {
    const cur = messages[index] as ThreadedMessage & { authorId?: number };
    const prev = messages[index - 1] as ThreadedMessage & { authorId?: number };
    return index > 0 && cur?.authorId === prev?.authorId;
  };

  if (!sitter) {
    return <div className='container py-8' />;
  }

  const sitterActive = sitter.active !== false;

  return (
    <div className='container py-6 max-w-5xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-1 hidden sm:block'>
        {t('pages.messageThread.messageNameHeading', { name: sitter.firstName })}
      </h1>
      {messages.length === 0 && sitterActive && (
        <p className='italic mb-4 hidden sm:block'>{t('pages.messageThread.startConversation')}</p>
      )}

      <div
        id='sitter-topbar'
        className='us-card rounded p-3 mb-3 border border-neutral-200 shadow-sm bg-white sm:hidden flex items-start gap-3'
      >
        <a id={`sitter-${sitter.id}-topbar`} href={`/app/sitter/${sitter.id}`} className='flex-shrink-0'>
          <img
            alt={sitter.firstName ?? ''}
            src={sitter.pictureUrl}
            className='w-14 h-14 rounded-full object-cover'
          />
        </a>
        <div className='flex-1 min-w-0 text-sm'>
          {sitterActive ? (
            <a
              id='sitter-name-to-profile-topbar'
              href={`/app/sitter/${sitter.id}`}
              className='font-semibold block'
            >
              {sitter.firstName}
            </a>
          ) : (
            <h3 className='font-semibold mb-0'>
              {t('common.noLongerActive', { name: sitter.firstName })}
            </h3>
          )}
          <div className='text-neutral-600'>
            {t('common.bookingCount', {
              count: sitter.relationship?.bookingCount ?? 0,
            })}
            {sitter.address?.city && <><br />{sitter.address.city}</>}
            {sitter.relationship?.distance && (
              <span> ({DisplayUtil.getDistanceText(t, sitter.relationship.distance)})</span>
            )}
          </div>
          {sitterActive && (
            <div className='flex flex-wrap gap-2 mt-2'>
              {sitter.relationship?.bookingCount ? (
                <Button
                  id='book-again-topbar'
                  buttonType={ButtonType.secondary}
                  buttonSize={ButtonSize.small}
                  className='!min-w-0 px-3'
                  onClick={bookSitter}
                >
                  {t('pages.messageThread.bookAgain')}
                </Button>
              ) : (
                <>
                  <Button
                    id='book-sitter-topbar'
                    buttonType={ButtonType.secondary}
                    buttonSize={ButtonSize.small}
                    className='!min-w-0 px-3'
                    onClick={bookSitter}
                  >
                    {t('pages.messageThread.book')}
                  </Button>
                  <Button
                    id='interview-sitter-topbar'
                    buttonType={ButtonType.secondary}
                    buttonSize={ButtonSize.small}
                    className='!min-w-0 px-3'
                    onClick={interviewSitter}
                  >
                    {t('pages.messageThread.interview')}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        <div id='dropdown-topbar' className='relative flex-shrink-0'>
          <Button
            buttonType={ButtonType.secondary}
            buttonSize={ButtonSize.small}
            className='!min-w-0 !px-2 !py-1'
            aria-label={t('common.moreOptionsAriaLabel')}
            onClick={(): void => setDropdownOpen((o) => !o)}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
          {dropdownOpen && (
            <>
              <div
                className='fixed inset-0 z-10'
                onClick={(): void => setDropdownOpen(false)}
                onKeyDown={(): void => setDropdownOpen(false)}
                role='presentation'
              />
              <ul
                className='absolute right-0 mt-1 bg-white border border-neutral-200 rounded shadow-lg z-20 min-w-max list-none p-0'
                role='menu'
              >
                <li role='menuitem'>
                  <a
                    href={`/app/bookings?filterType=all_active&sitterId=${sitter.id}`}
                    className='flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 text-sm no-underline text-inherit'
                    onClick={(): void => setDropdownOpen(false)}
                  >
                    <FontAwesomeIcon icon={faCircleCheck} className='text-sm' />
                    {t('common.viewBookings')}
                  </a>
                </li>
                <li role='menuitem'>
                  <Button
                    buttonType={ButtonType.tertiary}
                    className='!flex items-center gap-2 !px-4 !py-2 hover:bg-neutral-100 !text-sm w-full !text-left !rounded-none'
                    onClick={(): void => { setDropdownOpen(false); setFlagOpen(true); }}
                  >
                    <FontAwesomeIcon icon={faFlag} className='text-sm' />
                    {t('common.reportIssue')}
                  </Button>
                </li>
              </ul>
            </>
          )}
        </div>
      </div>

      <div id='message-thread-container' className='grid grid-cols-1 sm:grid-cols-12 gap-4'>
        <div className='sm:col-span-8 md:col-span-9'>
          {messages.length > 0 && (
            <div
              ref={messagesScrollRef}
              className='us-card p-4 rounded border border-neutral-200 shadow-sm bg-white max-h-[60vh] overflow-y-auto'
            >
              {totalSize > messages.length && (
                <Button
                  buttonType={ButtonType.tertiary}
                  id='load-previous-messages'
                  onClick={fetchPrevious}
                  className='block text-center mb-3 underline cursor-pointer bg-transparent border-0 p-0 w-full'
                >
                  {t('pages.messageThread.viewPrevious')}
                </Button>
              )}
              {messages.map((msg, index) => {
                const authorId = (msg as ThreadedMessage & { authorId?: number }).authorId;
                const isSelf = authorId !== sitter.id;
                const isRepeat = isSameSender(index);
                return (
                  <MessageBubble
                    key={msg.id ?? index}
                    id={`message-${index}`}
                    isSelf={isSelf}
                    isRepeat={isRepeat}
                    message={parseMessage(msg.message)}
                    label={msg.label}
                    avatarSrc={sitter.pictureUrl}
                    avatarAlt={sitter.firstName ?? ''}
                    avatarHref={`/app/sitter/${sitter.id}`}
                  />
                );
              })}
              <div ref={endRef} />
            </div>
          )}

          {sitterActive && (
            <MessageComposer
              value={newMessage}
              onChange={setNewMessage}
              onSend={createMessage}
              isSending={isSending}
              maxLength={MAX_MESSAGE_LENGTH}
              recipientName={sitter.firstName}
              ariaLabel={t('pages.messageThread.messageLabelTo', { name: sitter.firstName })}
              placeholder={t('pages.messageThread.typeMessage', { name: sitter.firstName })}
              sendAriaLabel={t('pages.messageThread.sendAriaLabel')}
            />
          )}
        </div>

        {/* Sitter sidebar */}
        <aside id='sitter-sidebar' className='sm:col-span-4 md:col-span-3 hidden sm:block'>
          <div id='sitter-sidebar-card' className='us-card rounded p-4 border border-neutral-200 shadow-sm bg-white'>
            <div className='relative inline-block mb-3'>
              <a id={`sitter-${sitter.id}`} href={`/app/sitter/${sitter.id}`}>
                <img
                  alt={sitter.firstName ?? ''}
                  src={sitter.pictureUrl}
                  className='w-20 h-20 rounded-full object-cover'
                />
              </a>
              {sitterActive && (
                <AvatarFavoriteButton
                  isFavorite={!!sitter.relationship?.bookmarked}
                  onToggle={toggleFavorite}
                />
              )}
            </div>
            {sitterActive ? (
              <a
                id='sitter-name-to-profile-sidebar'
                href={`/app/sitter/${sitter.id}`}
                className='font-semibold block mb-1'
              >
                {sitter.firstName}
              </a>
            ) : (
              <p className='font-semibold mb-1'>
                {t('common.noLongerActive', { name: sitter.firstName })}
              </p>
            )}
            <p className='text-sm text-neutral-600 mb-2'>
              {t('common.bookingCount', {
                count: sitter.relationship?.bookingCount ?? 0,
              })}
              {sitter.address?.city && <><br />{sitter.address.city}</>}
              {sitter.relationship?.distance && (
                <span> ({DisplayUtil.getDistanceText(t, sitter.relationship.distance)})</span>
              )}
            </p>
            <PrivateNoteSection
              note={sitter.relationship?.parentNote}
              onEdit={(): void => setNoteOpen(true)}
            />
            {sitterActive && (
              <div className='flex gap-2 items-center flex-nowrap'>
                {sitter.relationship?.bookingCount ? (
                  <Button
                    id='rebook-sitter'
                    buttonType={ButtonType.secondary}
                    buttonSize={ButtonSize.small}
                    className='!min-w-0 px-3'
                    onClick={bookSitter}
                  >
                    {t('pages.messageThread.bookAgain')}
                  </Button>
                ) : (
                  <>
                    <Button
                      id='book-sitter'
                      buttonType={ButtonType.secondary}
                      buttonSize={ButtonSize.small}
                      className='!min-w-0 px-3'
                      onClick={bookSitter}
                    >
                      {t('pages.messageThread.book')}
                    </Button>
                    <Button
                      id='interview-sitter'
                      buttonType={ButtonType.secondary}
                      buttonSize={ButtonSize.small}
                      className='!min-w-0 px-3'
                      onClick={interviewSitter}
                    >
                      {t('pages.messageThread.interview')}
                    </Button>
                  </>
                )}
                <div id='sidebar-dropdown' className='relative inline-block'>
                  <Button
                    buttonType={ButtonType.secondary}
                    buttonSize={ButtonSize.small}
                    className='!min-w-0 !px-2'
                    aria-label={t('common.moreOptionsAriaLabel')}
                    onClick={(): void => setDropdownOpen((o) => !o)}
                  >
                    <FontAwesomeIcon icon={faChevronDown} />
                  </Button>
                  {dropdownOpen && (
                    <>
                      <div
                        className='fixed inset-0 z-10'
                        onClick={(): void => setDropdownOpen(false)}
                        onKeyDown={(): void => setDropdownOpen(false)}
                        role='presentation'
                      />
                      <ul
                        className='absolute right-0 mt-1 bg-white border border-neutral-200 rounded shadow-lg z-20 min-w-max list-none p-0'
                        role='menu'
                      >
                        <li role='menuitem'>
                          <a
                            href={`/app/bookings?filterType=all_active&sitterId=${sitter.id}`}
                            className='flex items-center gap-2 px-4 py-2 hover:bg-neutral-100 text-sm no-underline text-inherit'
                            onClick={(): void => setDropdownOpen(false)}
                          >
                            <FontAwesomeIcon icon={faCircleCheck} className='text-sm' />
                            View bookings
                          </a>
                        </li>
                        <li role='menuitem'>
                          <Button
                            buttonType={ButtonType.tertiary}
                            className='!flex items-center gap-2 !px-4 !py-2 hover:bg-neutral-100 !text-sm w-full !text-left !rounded-none'
                            onClick={(): void => { setDropdownOpen(false); setFlagOpen(true); }}
                          >
                            <FontAwesomeIcon icon={faFlag} className='text-sm' />
                            Report an issue
                          </Button>
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {user?.id && sitter && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={handlePaywallSuccess}
          sitter={sitter as Sitter}
          userId={user.id}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
        />
      )}
      {sitter.id && user?.id && (
        <SitterNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={sitter.id}
          parentId={user.id}
          initialNote={sitter.relationship?.parentNote}
          onSaved={onNoteSaved}
        />
      )}
      {sitter.id && user?.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={sitter.id}
          flaggingId={user.id}
        />
      )}

      <Modal
        isOpen={showHiddenModal}
        onClose={(): void => setShowHiddenModal(false)}
        title={t('pages.messageThread.hiddenHeading')}
        className='w-full max-w-md'
        footer={
          <Button
            buttonType={ButtonType.primary}
            className='px-6'
            onClick={(): void => setShowHiddenModal(false)}
          >
            {t('pages.messageThread.hiddenOk')}
          </Button>
        }
      >
        <p>{t('pages.messageThread.hiddenBody')}</p>
      </Modal>
    </div>
  );
});
